// COGS explorer — action recipes.
//
// Each credit-consuming action is modeled as one or more "driver steps". A step
// binds to a swappable model *slot* (e.g. the research LLM), carries a default
// per-call usage assumption (tokens / chars / calls), and lists which models can
// serve that slot so the UI can offer a model-swap and recompute COGS live.
//
// `id` matches the backend usage row's `action` field and the /adm/get_users
// counter used as a cumulative volume proxy (see actions.ts → VOLUME_COUNTER).

export interface UsageAssumption {
  inputTokens?: number;
  outputTokens?: number;
  chars?: number;
  /** Number of driver invocations per one action (default 1). */
  calls?: number;
}

export interface DriverStep {
  slot: string;
  label: string;
  candidateModelIds: string[];
  defaultModelId: string;
  /** Apply the model's grounding surcharge (Gemini Search grounding). */
  grounded?: boolean;
  usage: UsageAssumption;
}

export interface CogsAction {
  id: string;
  label: string;
  /** Credits charged to the user for one action (0 = not credit-gated). */
  credits: number;
  /** Which /adm/get_users counter approximates cumulative volume, if any. */
  volumeCounter?:
    | 'fa_analysis'
    | 'prospect_research'
    | 'prospect_search'
    | 'prospect_messages';
  volumeCounterNote?: string;
  steps: DriverStep[];
}

const LLM_CANDIDATES = [
  'gemini-3-pro',
  'gemini-3-flash',
  'gemini-3.1-flash-lite',
  'gemini-2.5-pro',
  'gemini-2.5-flash',
  'gemini-2.5-flash-lite',
  'claude-opus-4-8',
  'claude-sonnet-5',
  'claude-haiku-4-5',
  'gpt-5.1',
  'deepseek-v3',
  'llama-3.3-70b',
];

export const DEFAULT_ACTIONS: CogsAction[] = [
  {
    id: 'prospect_research',
    label: 'Prospect research',
    credits: 2,
    volumeCounter: 'prospect_research',
    steps: [
      {
        slot: 'research_llm',
        label: 'Research LLM (grounded)',
        candidateModelIds: LLM_CANDIDATES,
        defaultModelId: 'gemini-2.5-pro',
        grounded: true,
        usage: { inputTokens: 6000, outputTokens: 1500, calls: 1 },
      },
    ],
  },
  {
    id: 'generate_prospect_messages',
    label: 'Message generation',
    credits: 2,
    volumeCounter: 'prospect_messages',
    steps: [
      {
        slot: 'message_llm',
        label: 'Message LLM (grounded)',
        candidateModelIds: LLM_CANDIDATES,
        defaultModelId: 'gemini-2.5-pro',
        grounded: true,
        usage: { inputTokens: 4000, outputTokens: 1200, calls: 1 },
      },
    ],
  },
  {
    id: 'enrich_prospect',
    label: 'Prospect enrichment',
    credits: 1,
    volumeCounter: 'prospect_search',
    volumeCounterNote: 'Uses prospect_search counter as a volume proxy (no direct enrich counter).',
    steps: [
      {
        slot: 'enrichment',
        label: 'Contact enrichment',
        candidateModelIds: ['apollo-enrich', 'perplexity-sonar'],
        defaultModelId: 'apollo-enrich',
        usage: { calls: 1 },
      },
    ],
  },
  {
    id: 'fa_analysis',
    label: 'FA analysis',
    credits: 1,
    volumeCounter: 'fa_analysis',
    steps: [
      {
        slot: 'fa_llm',
        label: 'FA analysis LLM (grounded)',
        candidateModelIds: LLM_CANDIDATES,
        defaultModelId: 'gemini-2.5-pro',
        grounded: true,
        usage: { inputTokens: 3000, outputTokens: 1500, calls: 1 },
      },
    ],
  },
  {
    id: 'coaching_turn',
    label: 'AskArchie coaching turn',
    credits: 0,
    volumeCounterNote: 'No cumulative counter yet — volume arrives via Sync.',
    steps: [
      {
        slot: 'coaching_llm',
        label: 'Coaching LLM',
        candidateModelIds: ['claude-opus-4-8', 'claude-sonnet-5', 'claude-haiku-4-5', 'gemini-3-flash', 'deepseek-v3'],
        defaultModelId: 'claude-opus-4-8',
        usage: { inputTokens: 8000, outputTokens: 800, calls: 1 },
      },
      {
        slot: 'rag_embedding',
        label: 'RAG retrieval embedding',
        candidateModelIds: ['text-embedding-3-small', 'text-embedding-3-large'],
        defaultModelId: 'text-embedding-3-small',
        usage: { inputTokens: 500, calls: 1 },
      },
      {
        slot: 'tts',
        label: 'Voice synthesis',
        candidateModelIds: ['eleven-multilingual-v2'],
        defaultModelId: 'eleven-multilingual-v2',
        usage: { chars: 800, calls: 1 },
      },
    ],
  },
];

/** Default slot→model selection derived from the action recipes. */
export function defaultSelection(actions: CogsAction[]): Record<string, string> {
  const sel: Record<string, string> = {};
  for (const a of actions) for (const s of a.steps) sel[s.slot] = s.defaultModelId;
  return sel;
}

/** All swappable slots, in display order, with their candidate lists. */
export function allSlots(actions: CogsAction[]): DriverStep[] {
  const seen = new Set<string>();
  const out: DriverStep[] = [];
  for (const a of actions) {
    for (const s of a.steps) {
      if (!seen.has(s.slot)) {
        seen.add(s.slot);
        out.push(s);
      }
    }
  }
  return out;
}
